import { useEffect, useState } from 'react';
import { useStores } from '@/hooks/use-stores';
import { message, Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import compress from 'browser-image-compression';
import { heicTo } from 'heic-to/csp';
import { useTranslation } from 'react-i18next';
import { ImageAdd } from '@/store/ImageUpload/ImageUploadStore';
import { MAX_IMAGE_UPLOAD_SIZE_MB, MAX_IMAGE_UPLOAD_SIZE } from '../ImageUpload.consts';

type Params = {
  onComplete?: () => void;
};

export const useImageCompression = ({ onComplete }: Params) => {
  const { imageUploadStore } = useStores();
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'success' | 'pending' | 'error'>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const [compressedFiles, setCompressedFiles] = useState<ImageAdd[]>([]);
  const [showFileLimitError, setShowFileLimitError] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);
  const { t } = useTranslation();

  const compressImage = async (file: RcFile, files: RcFile[]) => {
    if (files.length + imageUploadStore.images.length > imageUploadStore.maxFiles) {
      setShowFileLimitError(true);
      return Upload.LIST_IGNORE;
    }

    setTotalFiles(files.length);
    setIsLoading(true);
    setInitiated(true);
    setShowFileLimitError(false);

    setUploadStatus(prev => ({ ...prev, [file.uid]: 'pending' }));

    if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
      setUploadStatus(prev => ({ ...prev, [file.uid]: 'error' }));
      message.warning(t('uploadImageMaxSizeError', { filename: file.name, maxSize: MAX_IMAGE_UPLOAD_SIZE_MB }));
      return Upload.LIST_IGNORE;
    }

    try {
      let fileToCompress: File = file;

      // Check both MIME type and file extension (Windows browsers may not set correct MIME type for HEIC)
      const isHeic = ['image/heic', 'image/heif'].includes(file.type) || /\.(heic|heif)$/i.test(file.name);

      if (isHeic) {
        const converted = await heicTo({ blob: file, type: 'image/jpeg' });
        const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');

        fileToCompress = new File([Array.isArray(converted) ? converted[0] : converted], fileName, {
          type: 'image/jpeg',
        });
      }

      const compressedFile = await compress(fileToCompress, {
        maxSizeMB: 1,
        maxWidthOrHeight: 2048,
      });

      setCompressedFiles(prev => [...prev, { id: file.uid, file: compressedFile }]);
      setUploadStatus(prev => ({ ...prev, [file.uid]: 'success' }));
    } catch (error) {
      console.error('Error compressing image:', error);
      setUploadStatus(prev => ({ ...prev, [file.uid]: 'error' }));
      message.warning(t('uploadImageError', { filename: file.name }));
    }

    return Upload.LIST_IGNORE;
  };

  const reset = () => {
    setIsLoading(false);
    setUploadStatus({});
    setCompressedFiles([]);
    setInitiated(false);
    setTotalFiles(0);
    setShowFileLimitError(false);
  };

  useEffect(() => {
    if (!initiated || !Object.keys(uploadStatus).length) {
      return;
    }

    const allUploadsFinished = Object.values(uploadStatus).every(status => status !== 'pending');

    if (allUploadsFinished) {
      imageUploadStore.addImages(compressedFiles);
      reset();

      onComplete?.();
    }
  }, [initiated, uploadStatus, compressedFiles]);

  return {
    compressImage,
    isLoading,
    totalFiles,
    completedFiles: compressedFiles.length,
    showFileLimitError,
    reset,
    setShowFileLimitError,
  };
};
